import { Component, signal, OnInit, OnDestroy, inject, NgZone } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from './services/theme.service';
import {
  applyDocumentLanguage,
  normalizeLanguage,
  resolveInitialLanguage,
} from './shared/utils/app-language';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  template: `
    <div
      class="ptr-indicator"
      [class.ptr-indicator--visible]="pullVisible"
      [class.ptr-indicator--refreshing]="pullRefreshing"
      [style.transform]="'translateY(' + pullOffsetPx + 'px)'"
      aria-hidden="true"
    >
      <div class="ptr-icon">
        <svg class="ptr-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <polyline points="19 12 12 19 5 12"/>
        </svg>
        <svg class="ptr-spinner" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="4"
            stroke-dasharray="80 40"/>
        </svg>
      </div>
    </div>
    <router-outlet></router-outlet>
  `,
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('app');
  private readonly themeService = inject(ThemeService);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly pullToRefreshBreakpoint = 589;
  private readonly pullToRefreshThreshold = 90;
  private touchStartY = 0;
  private touchCurrentY = 0;
  private touchStartTarget: EventTarget | null = null;
  private isTrackingPull = false;

  pullVisible = false;
  pullRefreshing = false;
  pullOffsetPx = -64;

  private readonly onTouchStart = (event: TouchEvent): void => {
    if (!this.isPullToRefreshEnabled()) {
      return;
    }

    if (!this.isAtTopForTarget(event.target)) {
      return;
    }

    this.touchStartY = event.touches[0]?.clientY ?? 0;
    this.touchCurrentY = this.touchStartY;
    this.touchStartTarget = event.target;
    this.isTrackingPull = true;
  };

  private readonly onTouchMove = (event: TouchEvent): void => {
    if (!this.isTrackingPull) {
      return;
    }

    this.touchCurrentY = event.touches[0]?.clientY ?? this.touchCurrentY;
    const pullDistance = Math.max(0, this.touchCurrentY - this.touchStartY);

    if (!this.isAtTopForTarget(this.touchStartTarget)) {
      this.hidePullIndicator();
      this.resetPullTracking();
      return;
    }

    if (pullDistance > 0) {
      const clamped = Math.min(pullDistance, this.pullToRefreshThreshold * 1.5);
      const progress = Math.min(clamped / this.pullToRefreshThreshold, 1);
      this.zone.run(() => {
        this.pullVisible = true;
        this.pullRefreshing = progress >= 1;
        this.pullOffsetPx = -64 + clamped * 0.7;
      });
    }
  };

  private readonly onTouchEnd = (): void => {
    if (!this.isTrackingPull) {
      return;
    }

    const pullDistance = this.touchCurrentY - this.touchStartY;

    if (
      pullDistance >= this.pullToRefreshThreshold
      && this.isPullToRefreshEnabled()
      && this.isAtTopForTarget(this.touchStartTarget)
    ) {
      this.zone.run(() => {
        this.pullRefreshing = true;
        this.pullVisible = true;
        this.pullOffsetPx = 16;
      });
      this.resetPullTracking();
      setTimeout(() => globalThis.location.reload(), 350);
    } else {
      this.hidePullIndicator();
      this.resetPullTracking();
    }
  };

  /** Esconde el indicador; solo entra en la zona si de verdad estaba a la vista. */
  private hidePullIndicator(): void {
    if (!this.pullVisible && !this.pullRefreshing && this.pullOffsetPx === -64) return;
    this.zone.run(() => {
      this.pullVisible = false;
      this.pullRefreshing = false;
      this.pullOffsetPx = -64;
    });
  }

  ngOnInit(): void {
    this.initializeLanguage();
    this.themeService.initializeTheme();
    this.initializePullToRefresh();
  }

  /**
   * Refleja el idioma de la interfaz en `<html lang>`.
   *
   * Quién elige el idioma y quién carga el fichero es `provideTranslateService`
   * en `app.config.ts`. Aquí solo se sincroniza el atributo, que es lo que leen
   * el lector de pantalla, el corrector del navegador y la traducción automática.
   *
   * Va en este componente y no en `AppComponent` porque el raíz que arranca
   * `main.ts` es este: `AppComponent` no lo instancia nadie.
   */
  private initializeLanguage(): void {
    applyDocumentLanguage(resolveInitialLanguage());
    this.translate.onLangChange.subscribe(({ lang }) => {
      const language = normalizeLanguage(lang);
      if (language) applyDocumentLanguage(language);
    });
  }

  ngOnDestroy(): void {
    this.removePullToRefresh();
  }

  /**
   * Fuera de la zona de Angular: registrados dentro, CADA touchstart/touchmove/touchend de
   * la app lanzaba una detección de cambios completa. En iPhone eso, sumado a vistas que
   * recrean DOM en cada pase (/metrics), hacía que Safari se tragara los clics: la app se
   * quedaba «pillada» en el dashboard. Los handlers vuelven a la zona solo cuando cambia
   * el indicador.
   */
  private initializePullToRefresh(): void {
    this.zone.runOutsideAngular(() => {
      globalThis.addEventListener('touchstart', this.onTouchStart, { passive: true });
      globalThis.addEventListener('touchmove', this.onTouchMove, { passive: true });
      globalThis.addEventListener('touchend', this.onTouchEnd, { passive: true });
      globalThis.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    });
  }

  private removePullToRefresh(): void {
    globalThis.removeEventListener('touchstart', this.onTouchStart);
    globalThis.removeEventListener('touchmove', this.onTouchMove);
    globalThis.removeEventListener('touchend', this.onTouchEnd);
    globalThis.removeEventListener('touchcancel', this.onTouchEnd);
  }

  private isPullToRefreshEnabled(): boolean {
    return globalThis.innerWidth <= this.pullToRefreshBreakpoint;
  }

  private isAtTopForTarget(target: EventTarget | null): boolean {
    const scrollContainer = this.getClosestScrollableContainer(target) ?? this.getPrimaryScrollContainer();
    if (scrollContainer) {
      return scrollContainer.scrollTop <= 0;
    }

    return this.isWindowAtTop();
  }

  private isWindowAtTop(): boolean {
    const scrollTop = globalThis.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    return scrollTop <= 0;
  }

  private getPrimaryScrollContainer(): HTMLElement | null {
    const mainContent = document.querySelector('.synapse-main-content');
    if (mainContent instanceof HTMLElement && mainContent.scrollHeight > mainContent.clientHeight) {
      return mainContent;
    }

    return null;
  }

  private getClosestScrollableContainer(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) {
      return null;
    }

    let current: Element | null = target;

    while (current && current !== document.body) {
      if (current instanceof HTMLElement) {
        const style = globalThis.getComputedStyle(current);
        const overflowY = style.overflowY;
        const canScroll = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
          && current.scrollHeight > current.clientHeight;

        if (canScroll) {
          return current;
        }
      }

      current = current.parentElement;
    }

    return null;
  }

  private resetPullTracking(): void {
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.touchStartTarget = null;
    this.isTrackingPull = false;
  }
}
